import { MutableRefObject, useCallback, useMemo, useRef } from 'react'

import { skipToken } from '@reduxjs/toolkit/query'
import { FormApi } from 'final-form'

import {
  ALL_FLEXIBLE_NOTE_FIELDS,
  getMappedFlexibleNoteValues,
  isFlexibleNoteFormat,
} from '@lyrahealth-inc/shared-app-logic/src/features/notes/flexibleNoteFormatMapping'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Note, NoteStatusStates } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { actions, mixpanelEvents, sessionNoteTypes } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { useGetNotesQuery } from '../data/notesApi'
import { DA_FLEXIBLE_NOTE_NAME } from '../utils/noteConstants'

type UseLoadPreviousFlexibleNoteParams = {
  noteRef: MutableRefObject<FormApi<Dict> | null>
  note: Note
  episodeId: string | undefined
  appointments: Appointment[]
  isFlexibleNote: boolean
  noteIsCompleted: boolean
}

const getSessionNumber = (note: Note, appointments: Appointment[]): number => {
  const appointmentSessionNumber = appointments.find(
    (appointment) => appointment.appointmentId === note.appointment_id,
  )?.sessionNumber
  if (typeof appointmentSessionNumber === 'number') {
    return appointmentSessionNumber
  }
  const rawSessionNumber = note.body?.sessionNumber
  const sessionNumber = typeof rawSessionNumber === 'number' ? rawSessionNumber : parseInt(String(rawSessionNumber), 10)
  return Number.isFinite(sessionNumber) ? sessionNumber : -Infinity
}

export const useLoadPreviousFlexibleNote = ({
  noteRef,
  note,
  episodeId,
  appointments,
  isFlexibleNote,
  noteIsCompleted,
}: UseLoadPreviousFlexibleNoteParams) => {
  const shouldFetchEpisodeNotes = Boolean(isFlexibleNote && episodeId && !noteIsCompleted)
  const {
    data: episodeNotes,
    isSuccess: episodeNotesFetched,
    isError: episodeNotesErrored,
  } = useGetNotesQuery(shouldFetchEpisodeNotes && episodeId ? { episodeId } : skipToken)

  const previousFlexibleNote = useMemo(() => {
    if (!isFlexibleNote || noteIsCompleted || !episodeNotes) {
      return undefined
    }
    return [...episodeNotes]
      .filter(
        (candidate) =>
          candidate.content_meta_data?.name === DA_FLEXIBLE_NOTE_NAME &&
          candidate.status === NoteStatusStates.COMPLETED &&
          Boolean(candidate.submit_date) &&
          candidate.id !== note.id,
      )
      .sort((a, b) => {
        const sessionNumberDiff = getSessionNumber(b, appointments) - getSessionNumber(a, appointments)
        return sessionNumberDiff !== 0 ? sessionNumberDiff : (b.submit_date ?? '').localeCompare(a.submit_date ?? '')
      })[0]
  }, [isFlexibleNote, noteIsCompleted, episodeNotes, note.id, appointments])

  const canLoadPreviousNote = Boolean(isFlexibleNote && previousFlexibleNote)

  const previousNoteFormat = useMemo(() => {
    const format = previousFlexibleNote?.body?.noteFormat
    return isFlexibleNoteFormat(format) ? format : undefined
  }, [previousFlexibleNote])

  const isLoadingPreviousNoteFormat = shouldFetchEpisodeNotes && !episodeNotesFetched && !episodeNotesErrored

  const handleLoadPreviousNote = useCallback(() => {
    const form = noteRef.current
    if (!form || !previousFlexibleNote?.body) {
      return
    }
    const targetFormat = form.getFieldState?.('noteFormat')?.value
    if (!isFlexibleNoteFormat(targetFormat)) {
      return
    }
    const mappedValues = getMappedFlexibleNoteValues(previousFlexibleNote.body, targetFormat)
    form.batch(() => {
      Object.entries(mappedValues).forEach(([field, value]) => {
        form.mutators.setFormAttribute(field, value)
      })
    })
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.LOAD_PREVIOUS_FLEXIBLE_NOTE,
      details: {
        noteType: sessionNoteTypes[note.note_type],
        ...(note.appointment_id && { appointmentId: note.appointment_id.toString() }),
      },
    })
  }, [noteRef, previousFlexibleNote, note.note_type, note.appointment_id])

  const previousNoteFormatRef = useRef<string | undefined>(note.body?.noteFormat as string | undefined)
  const handleFlexibleNoteFormatChange = useCallback(
    (newFormat: string | undefined) => {
      const form = noteRef.current
      const prevFormat = previousNoteFormatRef.current
      previousNoteFormatRef.current = newFormat
      // Skip the initial value and no-op changes so we don't wipe an existing draft's content.
      if (!form || prevFormat === undefined || newFormat === prevFormat) {
        return
      }
      form.batch(() => {
        ALL_FLEXIBLE_NOTE_FIELDS.forEach((field) => {
          if (form.getFieldState?.(field)?.value) {
            form.mutators.setFormAttribute(field, '')
          }
        })
      })
    },
    [noteRef],
  )

  return {
    canLoadPreviousNote,
    handleLoadPreviousNote,
    handleFlexibleNoteFormatChange,
    previousNoteFormat,
    isLoadingPreviousNoteFormat,
  }
}
